import { useEffect, useState, useRef, useCallback } from 'react';
import './App.css';

const asset = (file) => `${process.env.PUBLIC_URL}/assets/hov/${file}`;

const heroSlides = [
  { file: 'hero-lounge.png', label: 'A timeless living space' },
  { file: 'hero-bed.png', label: 'A considered bedroom' },
  { file: 'hero-bedroom.png', label: 'A refined interior' },
];

const spaces = [
  { title: 'Residences', file: 'space-residences.png' },
  { title: 'Offices', file: 'space-offices.png' },
  { title: 'Retail', file: 'space-retail.png' },
  { title: 'Hospitality', file: 'space-hospitality.png' },
  { title: 'Institutions', file: 'space-institutions.png' },
  { title: 'Restaurants', file: 'space-restaurants.png' },
];

const services = [
  {
    title: 'Bespoke Furniture',
    file: 'service-bespoke.png',
    description:
      'Custom bespoke furniture designed and handcrafted to complement your space, lifestyle, and vision, with timeless design and exceptional craftsmanship.',
  },
  {
    title: 'Turnkey Interior Fit-Outs',
    file: 'service-turnkey.png',
    description:
      'Complete turnkey interior fit-out solutions, from design and planning to execution and installation, delivered with precision under one roof.',
  },
  {
    title: 'Design & Build',
    file: 'service-design-build.png',
    description:
      'Integrated design and build services that bring together interior design, furniture, manufacturing, and execution for seamless project delivery.',
  },
  {
    title: 'Joinery & Woodwork',
    file: 'service-joinery.png',
    description:
      'Precision custom joinery and woodwork, crafted for residential and commercial interiors with refined detailing and lasting quality.',
  },
  {
    title: 'Interior Project Management',
    file: 'service-management.png',
    description:
      'End-to-end interior project management, coordinating design, manufacturing, procurement, and execution to ensure timely and flawless delivery.',
  },
];

/* Critical images to preload immediately (above the fold + likely-viewed) */
const CRITICAL_IMAGES = [
  ...heroSlides.map((s) => s.file),
  'logo-white.png',
];

/* Secondary images to preload during loader (below the fold) */
const SECONDARY_IMAGES = [
  'about.png',
  'vision.png',
  ...spaces.map((s) => s.file),
  ...services.map((s) => s.file),
  'furniture.png',
  'detail-quote.png',
];

/* Preload helper */
const preloadImage = (src) =>
  new Promise((resolve) => {
    const img = new Image();
    img.onload = resolve;
    img.onerror = resolve; // don't block on errors
    img.src = src;
  });

function App() {
  const [loading, setLoading] = useState(true);
  const [logoFade, setLogoFade] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const [heroIndex, setHeroIndex] = useState(0);
  const [serviceIndex, setServiceIndex] = useState(0);
  const [formSent, setFormSent] = useState(false);
  const [headerScrolled, setHeaderScrolled] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null); // number 1–12, or null

  // Scroll-driven spaces
  const spacesWrapRef = useRef(null);
  const [activeSpace, setActiveSpace] = useState(0);
  const [spaceScrollProgress, setSpaceScrollProgress] = useState([]);
  const [showTopBtn, setShowTopBtn] = useState(false);

  const openFurnitureLightbox = (imageNum) => {
    setLightboxImage(imageNum);
  };

  const closeFurnitureLightbox = () => {
    setLightboxImage(null);
  };

  const handleSpaceScroll = useCallback(() => {
    const wrap = spacesWrapRef.current;
    if (!wrap) return;

    const wrapRect = wrap.getBoundingClientRect();
    const wrapTop = wrapRect.top;
    const viewH = window.innerHeight;
    const totalScroll = wrap.scrollHeight - viewH;
    const scrolled = -wrapTop;
    const count = spaces.length;
    const segmentSize = totalScroll / count;

    const progresses = [];
    let currentActive = 0;

    for (let i = 0; i < count; i++) {
      const segStart = i * segmentSize;
      const segEnd = (i + 1) * segmentSize;

      if (scrolled < segStart) {
        progresses.push(0);
      } else if (scrolled >= segEnd) {
        progresses.push(1);
        currentActive = i;
      } else {
        const p = (scrolled - segStart) / segmentSize;
        progresses.push(p);
        currentActive = i;
      }
    }

    setSpaceScrollProgress(progresses);
    setActiveSpace(currentActive);
  }, []);

  /* ═══ Image Preloading ═══
     Preload critical + secondary images WHILE the video loader plays,
     so by the time the site is revealed everything is cached.
  */
  useEffect(() => {
    // Preload critical images immediately (parallel)
    Promise.all(CRITICAL_IMAGES.map((f) => preloadImage(asset(f))));

    // Preload secondary images slightly delayed to not compete with critical
    const t = setTimeout(() => {
      SECONDARY_IMAGES.forEach((f) => preloadImage(asset(f)));
    }, 500);

    return () => clearTimeout(t);
  }, []);

  /* Loader — safety fallback only (if video fails to load) */
  useEffect(() => {
    const fallbackTimer = setTimeout(() => {
      setLogoFade(true);
      setTimeout(() => setLoading(false), 800);
    }, 30000);
    return () => clearTimeout(fallbackTimer);
  }, []);

  /* Lock scroll + Escape closes modal */
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape') setModalOpen(false);
    };
    if (modalOpen) {
      document.body.style.overflow = 'hidden';
      window.addEventListener('keydown', handler);
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handler);
    };
  }, [modalOpen]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape') closeFurnitureLightbox();
    };

    if (lightboxImage) {
      document.body.style.overflow = 'hidden';
      window.addEventListener('keydown', onKeyDown);
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [lightboxImage]);

  /* Hero auto-advance */
  useEffect(() => {
    const timer = window.setInterval(() => {
      setHeroIndex((c) => (c + 1) % heroSlides.length);
    }, 6500);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    const onScroll = () => {
      setHeaderScrolled(window.scrollY > 60);
      setShowTopBtn(window.scrollY > 500);
      handleSpaceScroll();
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll(); // trigger once on mount
    return () => window.removeEventListener('scroll', onScroll);
  }, [handleSpaceScroll]);

  /* Escape closes nav */
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape') setNavOpen(false);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const goTo = (id) => {
    setNavOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const submitEnquiry = (e) => {
    e.preventDefault();
    e.currentTarget.reset();
    setFormSent(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    // Reset form-sent state after fade-out
    setTimeout(() => setFormSent(false), 400);
  };

  /* ── Loader ── */
  if (loading) {
    return (
      <div className={`loader ${logoFade ? 'loader--fade' : ''}`}>
        <video
          className="loader-video"
          src={`${process.env.PUBLIC_URL}/videoloader.mp4`}
          autoPlay
          muted
          playsInline
          preload="auto"
          onEnded={() => {
            setLogoFade(true);
            setTimeout(() => setLoading(false), 800);
          }}
        />
      </div>
    );
  }

  return (
    <div className="site-shell">
      {/* ── Header ── */}
      <header className={`site-header ${headerScrolled ? 'site-header--scrolled' : ''}`}>
        <div className="header-inner">
          <button className="header-brand" onClick={() => goTo('home')} aria-label="House of Vishwakarma home">
            HOUSE OF VISHWAKARMA
          </button>

          <button className="header-logo" onClick={() => goTo('home')} aria-label="House of Vishwakarma home">
            <img src={asset('logo-white-cropped.png')} alt="House of Vishwakarma" fetchpriority="high" decoding="async" />
          </button>

          <nav className="header-nav" aria-label="Main navigation desktop">
            {[
              ['Home', 'home'],
              ['Know Us', 'know-us'],
              ['Furniture', 'furniture'],
              ['Contact', 'contact'],
            ].map(([label, id]) => (
              <button key={id} onClick={() => goTo(id)}>{label}</button>
            ))}
          </nav>

          <button
            className={`menu-toggle ${navOpen ? 'menu-toggle--open' : ''}`}
            onClick={() => setNavOpen((o) => !o)}
            aria-expanded={navOpen}
            aria-controls="mobile-navigation"
          >
            <i /><i /><i />
          </button>
        </div>

        <nav
          id="mobile-navigation"
          className={`mobile-nav ${navOpen ? 'mobile-nav--open' : ''}`}
          aria-label="Main navigation mobile"
        >
          {[
            ['Home', 'home'],
            ['Know Us', 'know-us'],
            ['Spaces', 'spaces'],
            ['Services', 'services'],
            ['Furniture', 'furniture'],
            ['Contact', 'contact'],
          ].map(([label, id]) => (
            <button key={id} onClick={() => goTo(id)}>{label}</button>
          ))}
          <div className="mobile-nav-social">
            <a href="https://www.instagram.com/houseofvishwakarma/" target="_blank" rel="noreferrer">Instagram</a>
            <a href="https://www.linkedin.com/company/house-of-vishwakarma/" target="_blank" rel="noreferrer">LinkedIn</a>
          </div>
        </nav>
      </header>

      <main>
        {/* ── Hero ── */}
        <section className="hero" id="home" aria-label="House of Vishwakarma">
          <div className="hero-slides" aria-live="polite">
            {heroSlides.map((slide, i) => (
              <div
                key={slide.file}
                className={`hero-slide ${i === heroIndex ? 'hero-slide--active' : ''}`}
                style={{ backgroundImage: `url(${asset(slide.file)})` }}
                aria-hidden={i !== heroIndex}
              />
            ))}
          </div>
          <div className="hero-shade" />
          <div className="hero-content">
            <h1>Furniture <em>+ Interiors</em></h1>
          </div>
          <div className="hero-controls">
            {heroSlides.map((slide, i) => (
              <button
                key={slide.file}
                className={i === heroIndex ? 'is-active' : ''}
                onClick={() => setHeroIndex(i)}
                aria-label={`Show slide ${i + 1}: ${slide.label}`}
                aria-current={i === heroIndex ? 'true' : undefined}
              />
            ))}
          </div>
          {/* <span className="hero-count">0{heroIndex + 1} / 0{heroSlides.length}</span> */}
        </section>

        {/* ── Know Us ── */}
        <section className="intro" id="know-us">
          {/* ─── About Us — text LEFT, image RIGHT ─── */}
          <div className="intro-row">
            <div className="intro-row__text">
              <h2 className="intro-row__title">
                <span className="intro-brand-serif">House of Vishwakarma</span>
                <span className="intro-brand-sans">Furniture + Interiors</span>
              </h2>
              <p className="intro-row__body">
                The best spaces are not assembled. They are carefully made. That belief
                has shaped House of Vishwakarma for over four decades. From bespoke
                furniture to complete interior environments, every project is crafted
                under one roof through thoughtful design, skilled artisanship, and an
                uncompromising attention to detail, resulting in spaces that are
                timeless in character and made to endure.
              </p>
              <button className="intro-cta" onClick={() => goTo('contact')}>
                Connect With Us <span aria-hidden="true">→</span>
              </button>
            </div>
            <div className="intro-row__image">
              <img
                src={asset('about.png')}
                alt="House of Vishwakarma craftsmanship"
                loading="lazy"
                decoding="async"
              />
            </div>
          </div>

          {/* ─── Our Vision — image LEFT, text RIGHT ─── */}
          <div className="intro-row">
            <div className="intro-row__image">
              <img
                src={asset('vision.png')}
                alt="Our Vision"
                loading="lazy"
                decoding="async"
              />
            </div>
            <div className="intro-row__text">
              <h2 className="intro-row__title">
                <span className="intro-brand-serif">Our Vision</span>
              </h2>
              <p className="intro-row__body">
                To us, the finest spaces are defined by intention, not excess. We
                envision spaces that feel as meaningful years from now as they do the
                day they are created. Our vision is to create environments where
                craftsmanship, material, and design exist in quiet harmony, enriching
                everyday life for generations.
              </p>
              <button className="intro-cta" onClick={() => goTo('contact')}>
                Connect With Us <span aria-hidden="true">→</span>
              </button>
            </div>
          </div>
        </section>

        {/* ── Spaces — Scroll-driven stacking with title overlay ── */}
        <section className="spaces-scroll-wrap" id="spaces" ref={spacesWrapRef}>
          <div className="spaces-sticky">
            <div className="spaces-stack">
              {spaces.map((space, i) => {
                const progress = spaceScrollProgress[i] || 0;
                const isVisible = i === 0 || progress > 0;
                const translateY = i === 0 ? 0 : (1 - progress) * 100;
                const scale = i < activeSpace ? 0.92 : 1;

                return (
                  <div
                    key={space.file}
                    className="spaces-stack__layer"
                    style={{
                      zIndex: i + 1,
                      transform:
                        i === 0
                          ? `scale(${i < activeSpace ? 0.92 : 1})`
                          : `translateY(${translateY}%) scale(${scale})`,
                      opacity: isVisible ? 1 : 0,
                    }}
                  >
                    <img
                      src={asset(space.file)}
                      alt={space.title}
                      loading={i === 0 ? 'eager' : 'lazy'}
                      decoding="async"
                    />
                    <div className="spaces-stack__overlay" />
                    {/* Title overlay per image */}
                    <div className="spaces-stack__title-wrap">
                      <h2 className="spaces-stack__title">{space.title}</h2>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="spaces-dots">
              {spaces.map((space, i) => (
                <div
                  key={space.title}
                  className={`spaces-dot ${i === activeSpace ? 'spaces-dot--active' : ''} ${i < activeSpace ? 'spaces-dot--done' : ''}`}
                >
                  <span className="spaces-dot__line" />
                  <span className="spaces-dot__label">{space.title}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Detail Quote — Background image with inline quotes ── */}
        <section className="detail-quote" aria-label="Quote">
          <img
            className="detail-quote__bg"
            src={asset('detail-quote.png')}
            alt=""
            loading="lazy"
            decoding="async"
          />
          <div className="detail-quote__shade" />
          <div className="detail-quote__inner">
            <p className="detail-quote__text">
              “The Language of luxury is written in the details.”
            </p>
            <span className="detail-quote__line" aria-hidden="true" />
          </div>
        </section>

        {/* ── Services — Fullscreen slideshow with overlay text ── */}
        <section className="services" id="services">
          <div className="services-slideshow">
            <div className="services-slideshow__bg">
              {services.map((s, i) => (
                <div
                  key={s.file}
                  className={`services-slideshow__slide ${i === serviceIndex ? 'services-slideshow__slide--active' : ''}`}
                  style={{ backgroundImage: `url(${asset(s.file)})` }}
                  aria-hidden={i !== serviceIndex}
                />
              ))}
              <div className="services-slideshow__overlay" />
            </div>

            {/* Centered text content over image */}
            <div className="services-slideshow__content" key={`content-${serviceIndex}`}>
              <h2 className="services-slideshow__title">
                {services[serviceIndex].title}
              </h2>
              <p className="services-slideshow__desc">
                {services[serviceIndex].description}
              </p>
            </div>

            <button
              className="services-arrow services-arrow--prev"
              onClick={() => setServiceIndex((i) => (i - 1 + services.length) % services.length)}
              aria-label="Previous service"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M15 18l-6-6 6-6" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>

            <button
              className="services-arrow services-arrow--next"
              onClick={() => setServiceIndex((i) => (i + 1) % services.length)}
              aria-label="Next service"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M9 6l6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>

            <div className="services-slideshow__footer">
              <div className="services-slideshow__dots" role="tablist" aria-label="Services">
                {services.map((s, i) => (
                  <button
                    key={s.title}
                    className={`services-dot ${i === serviceIndex ? 'services-dot--active' : ''}`}
                    onClick={() => setServiceIndex(i)}
                    role="tab"
                    aria-selected={serviceIndex === i}
                    aria-label={s.title}
                  />
                ))}
              </div>
            </div>
          </div>
        </section>


        {/* ── Furniture — Auto-scrolling slider with hover zoom + lightbox ── */}
        <section className="furniture" id="furniture">
          <div className="furniture-header">
            <h2 className="furniture-header__title">
              Explore Furniture Collection <span className="furniture-header__amp">&amp;</span> Custom Design
            </h2>
            <p className="furniture-header__desc">
              From Collection to Custom. Discover ready-made designs or collaborate
              with us to create furniture that's uniquely yours.
            </p>
          </div>

          <div className="furniture-slider" aria-label="Furniture gallery">
            <div className="furniture-slider__track">
              {[...Array(12), ...Array(12)].map((_, idx) => {
                const imageNum = (idx % 12) + 1;
                return (
                  <div
                    className="furniture-slider__item"
                    key={`furn-${idx}`}
                    onClick={() => openFurnitureLightbox(imageNum)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        openFurnitureLightbox(imageNum);
                      }
                    }}
                    aria-label={`View furniture piece ${imageNum}`}
                  >
                    <img
                      src={asset(`${imageNum}.png`)}
                      alt={`Furniture piece ${imageNum}`}
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ── Clientele — Simple auto-scrolling logo marquee ── */}
        <section className="clientele" aria-labelledby="clientele-title">
          <h2
            id="clientele-title"
            className="section-title"
            style={{ textAlign: 'center' }}
          >
            CLIENTELE
          </h2>

          <div className="clientele-logos" aria-label="Our clients">
            <div className="clientele-logos__track">
              {[...Array(19), ...Array(19)].map((_, idx) => {
                const logoNum = (idx % 19) + 1;
                return (
                  <div className="clientele-logos__item" key={`client-${idx}`}>
                    <img
                      src={`${process.env.PUBLIC_URL}/assets/hov/clients/${logoNum}.png`}
                      alt={`Client ${logoNum}`}
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ── Contact + Footer ── */}
        <section className="contact" id="contact">
          <div className="contact-cta">
            <p className="contact-cta__kicker">Let&apos;s Create Something Timeless</p>
            <h2 className="contact-cta__heading">
              Whether you&apos;re looking for custom furniture or complete interior
              solutions, we&apos;re here to bring your vision to life.
            </h2>
            <button className="contact-cta__button" onClick={() => setModalOpen(true)}>
              Get in Touch <span aria-hidden="true">→</span>
            </button>
          </div>

          <footer className="site-footer" style={{ '--footer-bg-img': `url(${asset('hero-lounge.png')})` }}>
            <div className="footer-top">
              {/* ─── LEFT COLUMN — Brand + Description ─── */}
              <div className="footer-col footer-col--brand">
                <img
                  className="footer-logo"
                  src={asset('logo-white.png')}
                  alt="House of Vishwakarma"
                  loading="lazy"
                  decoding="async"
                />
                <p className="footer-description">
                  House of Vishwakarma specializes in custom furniture manufacturing,
                  modular solutions, interior renovation, and premium woodworking
                  services. We are committed to delivering beautifully crafted spaces
                  that combine style, comfort, and functionality for residential and
                  commercial clients across India.
                </p>
              </div>

              {/* ─── CENTER COLUMN — Quick Links ─── */}
              <nav className="footer-col footer-col--links" aria-label="Footer navigation">
                <h3 className="footer-heading">Quick Links</h3>
                <ul className="footer-nav-list">
                  {[
                    ['Home', 'home'],
                    ['Know Us', 'know-us'],
                    ['Furniture', 'furniture'],
                    ['Contact', 'contact'],
                  ].map(([label, id]) => (
                    <li key={id}>
                      <button onClick={() => goTo(id)}>{label}</button>
                    </li>
                  ))}
                </ul>
              </nav>

              {/* ─── RIGHT COLUMN — Contact info + Send Message ─── */}
              <div className="footer-col footer-col--contact">
                <h3 className="footer-heading footer-heading--right">
                  Get Exactly What You Need
                  <br />
                  Call Now !
                </h3>

                <a
                  href="mailto:contacthouseofvishwakarma@gmail.com"
                  className="footer-contact-line"
                >
                  <span className="footer-icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <rect x="3" y="5" width="18" height="14" rx="2" />
                      <path d="M3 7l9 6 9-6" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </span>
                  contacthouseofvishwakarma@gmail.com
                </a>

                <a href="tel:+916290495459" className="footer-contact-line">
                  <span className="footer-icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path
                        d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                  +91 6290495459
                </a>

                <div className="footer-social">
                  <a
                    href="https://www.instagram.com/houseofvishwakarma/"
                    target="_blank"
                    rel="noreferrer"
                    aria-label="Instagram"
                    className="footer-social-icon"
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
                      <rect x="3" y="3" width="18" height="18" rx="5" />
                      <circle cx="12" cy="12" r="4" />
                      <circle cx="17.5" cy="6.5" r="0.6" fill="currentColor" />
                    </svg>
                  </a>
                  <a
                    href="https://www.facebook.com/"
                    target="_blank"
                    rel="noreferrer"
                    aria-label="Facebook"
                    className="footer-social-icon"
                  >
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M22 12a10 10 0 1 0-11.56 9.88v-6.99H7.9V12h2.54V9.8c0-2.5 1.49-3.89 3.77-3.89 1.09 0 2.24.2 2.24.2v2.46h-1.26c-1.24 0-1.63.77-1.63 1.56V12h2.77l-.44 2.89h-2.33v6.99A10 10 0 0 0 22 12z" />
                    </svg>
                  </a>
                  <a
                    href="https://www.linkedin.com/company/house-of-vishwakarma/"
                    target="_blank"
                    rel="noreferrer"
                    aria-label="LinkedIn"
                    className="footer-social-icon"
                  >
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M20.45 20.45h-3.55v-5.57c0-1.33-.02-3.04-1.85-3.04-1.86 0-2.14 1.45-2.14 2.94v5.67H9.36V9h3.41v1.56h.05a3.74 3.74 0 0 1 3.36-1.84c3.6 0 4.26 2.37 4.26 5.45v6.28zM5.34 7.43a2.06 2.06 0 1 1 0-4.12 2.06 2.06 0 0 1 0 4.12zM7.12 20.45H3.56V9h3.56v11.45zM22.22 0H1.77C.79 0 0 .77 0 1.72v20.56C0 23.23.79 24 1.77 24h20.44c.98 0 1.78-.77 1.78-1.72V1.72C24 .77 23.2 0 22.22 0z" />
                    </svg>
                  </a>
                  <a
                    href="https://www.pinterest.com/"
                    target="_blank"
                    rel="noreferrer"
                    aria-label="Pinterest"
                    className="footer-social-icon"
                  >
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 0C5.37 0 0 5.37 0 12c0 5.08 3.16 9.42 7.63 11.17-.11-.95-.2-2.42.04-3.46.22-.94 1.4-6 1.4-6s-.36-.72-.36-1.78c0-1.67.97-2.92 2.18-2.92 1.03 0 1.53.77 1.53 1.7 0 1.04-.66 2.59-1 4.03-.28 1.21.6 2.19 1.79 2.19 2.15 0 3.8-2.27 3.8-5.54 0-2.9-2.08-4.93-5.05-4.93-3.44 0-5.46 2.58-5.46 5.25 0 1.04.4 2.16.9 2.76.1.12.11.22.08.34-.09.37-.29 1.21-.33 1.38-.05.22-.17.27-.4.16-1.5-.7-2.44-2.89-2.44-4.65 0-3.79 2.75-7.27 7.93-7.27 4.16 0 7.4 2.97 7.4 6.93 0 4.14-2.61 7.47-6.23 7.47-1.22 0-2.36-.63-2.75-1.38l-.75 2.85c-.27 1.04-1 2.35-1.49 3.15C9.57 23.85 10.77 24 12 24c6.63 0 12-5.37 12-12S18.63 0 12 0z" />
                  </svg>
                  </a>
                </div>

                <button
                  className="footer-send-btn"
                  onClick={() => setModalOpen(true)}
                >
                  Send Message
                </button>
              </div>
            </div>

            {/* ─── Bottom bar ─── */}
            <div className="footer-bottom">
              <p>
                Copyright © {new Date().getFullYear()} All Rights Reserved to{' '}
                <span className="footer-bottom__brand">House of Vishwakarma</span>
                {' | '}
                Design And Developed By{' '}
                <a
                  // href=""
                  target="_blank"
                  rel="noreferrer"
                  className="footer-bottom__dev"
                >
                  SJ
                </a>
              </p>
            </div>
          </footer>
        </section>
      </main>
      {/* ═══ Enquiry Modal ═══ */}
      {modalOpen && (
        <div
          className="modal-backdrop"
          onClick={closeModal}
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-title"
        >
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal__close" onClick={closeModal} aria-label="Close modal">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" />
              </svg>
            </button>

            <h3 id="modal-title" className="modal__title">Get In Touch</h3>

            {formSent ? (
              <div className="modal__thanks" role="status">
                <div className="modal__thanks-icon">✓</div>
                <p className="modal__thanks-title">Thank you!</p>
                <p className="modal__thanks-text">
                  Our team will get in touch within 48 hours.
                </p>
                <button className="modal__thanks-close" onClick={closeModal}>
                  Close
                </button>
              </div>
            ) : (
              <form className="modal__form" onSubmit={submitEnquiry}>
                <label className="modal__field">
                  <span className="modal__label">Name</span>
                  <input
                    id="enquiry-name"
                    name="name"
                    type="text"
                    placeholder="Name"
                    required
                  />
                </label>

                <label className="modal__field">
                  <span className="modal__label">Phone</span>
                  <input name="phone" type="tel" placeholder="Phone" required />
                </label>

                <label className="modal__field">
                  <span className="modal__label">Email</span>
                  <input name="email" type="email" placeholder="Email" required />
                </label>

                <label className="modal__field">
                  <span className="modal__label">Services</span>
                  <select name="interest" defaultValue="" required>
                    <option value="" disabled>
                      You're Interested In (dropdown options)
                    </option>
                    <option>Custom Furniture</option>
                    <option>Ready Collection</option>
                    <option>Interiors</option>
                    <option>Project Collaboration</option>
                    <option>Other</option>
                  </select>
                </label>

                <button type="submit" className="modal__submit">
                  Submit
                </button>
              </form>
            )}
          </div>
        </div>
      )}
      {/* ═══ Back to Top button ═══ */}
      <button
        className={`back-to-top ${showTopBtn ? 'back-to-top--visible' : ''}`}
        onClick={scrollToTop}
        aria-label="Back to top"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M18 15l-6-6-6 6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {/* ═══ Furniture Lightbox ═══ */}
      {lightboxImage && (
        <div
          className="furniture-lightbox"
          onClick={closeFurnitureLightbox}
          role="dialog"
          aria-modal="true"
          aria-label="Furniture image preview"
        >
          {/* Close Button */}
          <button
            className="furniture-lightbox__close"
            onClick={closeFurnitureLightbox}
            aria-label="Close preview"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
              <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" />
            </svg>
          </button>

          {/* Previous Arrow (Left) */}
          <button
            className="furniture-lightbox__arrow furniture-lightbox__arrow--prev"
            onClick={(e) => {
              e.stopPropagation();
              setLightboxImage((n) => (n === 1 ? 12 : n - 1));
            }}
            aria-label="Previous image"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M15 19l-7-7 7-7" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>

          {/* Image Container */}
          <div
            className="furniture-lightbox__content"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={asset(`${lightboxImage}.png`)}
              alt={`Furniture piece ${lightboxImage}`}
              className="furniture-lightbox__image"
            />
            
            {/* Image Counter
            <span className="furniture-lightbox__count">
              {String(lightboxImage).padStart(2, '0')} / 12
            </span> */}
          </div>

          {/* Next Arrow (Right) */}
          <button
            className="furniture-lightbox__arrow furniture-lightbox__arrow--next"
            onClick={(e) => {
              e.stopPropagation();
              setLightboxImage((n) => (n === 12 ? 1 : n + 1));
            }}
            aria-label="Next image"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
}

export default App;